import { useState, useEffect } from "react";
import { AsYouType, parsePhoneNumber } from "libphonenumber-js";
import {
  DEFAULT_COUNTRY,
  getCountryConfig,
  validatePostalCode,
  formatPostalCode
} from "../constants/countries";

const countryNameToCode = {
  'United States': 'US',
  'Canada': 'CA',
  'United Kingdom': 'GB',
  'Australia': 'AU',
  'Mexico': 'MX'
};

const countryCodeToName = {
  'US': 'United States',
  'CA': 'Canada',
  'GB': 'United Kingdom',
  'AU': 'Australia',
  'MX': 'Mexico'
};

export const useCountryForm = (initialCountry = DEFAULT_COUNTRY) => {
  const [countryCode, setCountryCode] = useState(initialCountry);
  const [countryName, setCountryName] = useState(countryCodeToName[initialCountry]);
  const [countryConfig, setCountryConfig] = useState(getCountryConfig(initialCountry));

  useEffect(() => {
    setCountryConfig(getCountryConfig(countryCode));
  }, [countryCode]);

  const handleCountryChange = (selectedCountryName) => {
    const code = countryNameToCode[selectedCountryName] || DEFAULT_COUNTRY;
    setCountryCode(code);
    setCountryName(selectedCountryName);
  };

  const formatPhone = (phoneValue) => {
    try {
      const phoneFormatter = new AsYouType(countryConfig.phoneFormat);
      return phoneFormatter.input(phoneValue);
    } catch {
      return phoneValue;
    }
  };

  const validatePhone = (phoneValue) => {
    if (!phoneValue) return false;
    try {
      return parsePhoneNumber(phoneValue, countryConfig.phoneFormat).isValid();
    } catch {
      return false;
    }
  };

  const formatPhoneForSubmit = (phoneValue) => {
    try {
      return parsePhoneNumber(phoneValue, countryConfig.phoneFormat).format("E.164");
    } catch {
      return phoneValue;
    }
  };

  const validatePostal = (postalValue) => {
    return validatePostalCode(postalValue, countryCode);
  };

  const formatPostal = (postalValue) => {
    return formatPostalCode(postalValue, countryCode);
  };

  const getPostalCodeMaxLength = () => {
    return countryConfig.postalCodeMaxLength;
  };

  const handlePostalCodeChange = (value) => {
    if (countryCode === "US" || countryCode === "MX" || countryCode === "AU") {
      return value.replace(/\D/g, "").slice(0, getPostalCodeMaxLength());
    }
    
    if (countryCode === "CA") {
      const cleaned = value.replace(/[^A-Z0-9]/gi, "").toUpperCase();
      if (cleaned.length <= 6) {
        if (cleaned.length > 3) {
          return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        }
        return cleaned;
      }
      return value.slice(0, 7);
    }
    
    if (countryCode === "GB") {
      const cleaned = value.replace(/[^A-Z0-9]/gi, "").toUpperCase();
      if (cleaned.length <= 7) {
        return cleaned;
      }
      return value.slice(0, 8);
    }
    
    return value;
  };

  const getStateOptions = () => {
    return countryConfig.states.map(state => state.code);
  };

  const getStateLabel = () => {
    return countryConfig.stateLabel;
  };

  const getPostalCodeLabel = () => {
    return countryConfig.postalCodeLabel;
  };

  const getPostalCodePlaceholder = () => {
    return countryConfig.postalCodePlaceholder;
  };

  const getCountryOptions = () => {
    return ['United States', 'Canada']; // 'United Kingdom', 'Australia', 'Mexico'
  };

  return {
    country: countryCode,
    countryName,
    setCountry: handleCountryChange,
    countryConfig,
    getCountryOptions,
    formatPhone,
    validatePhone,
    formatPhoneForSubmit,
    validatePostal,
    formatPostal,
    handlePostalCodeChange,
    getStateOptions,
    getStateLabel,
    getPostalCodeLabel,
    getPostalCodePlaceholder,
    getPostalCodeMaxLength
  };
};